/**
 * CharacterForm Custom Element
 * Handles character details collection for personalized book builder
 */
const CHARACTER_FORM_PREVIEW_DEFAULTS = {
  magic: {
    illustrationId: "cmg9fa6qz002sl804dvy5t716",
    characterId: "cmg9fa516000tl8040m04lfw5",
  },
};
const CHARACTER_FORM_PREVIEW_ENABLED = false;

if (!customElements.get("character-form")) {
  customElements.define(
    "character-form",
    class CharacterForm extends HTMLElement {
      constructor() {
        super();
        this.uploadTimeout = null;
      }

      connectedCallback() {
        this.render();
        this.setupEventListeners();
        if (CHARACTER_FORM_PREVIEW_ENABLED) {
          this.setupPreview();
        }
      }

      // Use global translations object
      get strings() {
        return theme.personalizedBookStrings || {};
      }

      get sectionId() {
        return this.getAttribute("data-section-id");
      }

      get showStartBadge() {
        return this.getAttribute("data-show-start-badge") === "true";
      }

      get examplePhotoUrl() {
        return this.getAttribute("data-example-photo-url") || "";
      }

      get examplePhotoAlt() {
        return (
          this.getAttribute("data-example-photo-alt") ||
          this.strings.goodBadExamples ||
          "Examples of good and bad photos"
        );
      }

      get characterNumber() {
        return this.getAttribute("data-character-number") || "";
      }

      get stepNumber() {
        return this.getAttribute("data-step-number") || "1";
      }

      // Generate unique ID suffix for multi-character mode
      getFieldId(baseName) {
        const sectionId = this.sectionId;
        const characterNumber = this.characterNumber;
        // If we have a character number, use it: pbk-child-name-1-sectionId
        // Otherwise: pbk-child-name-sectionId
        return characterNumber
          ? `${baseName}-${characterNumber}-${sectionId}`
          : `${baseName}-${sectionId}`;
      }

      render() {
        const sectionId = this.sectionId;
        const showStartBadge = this.showStartBadge;
        const examplePhotoUrl = this.examplePhotoUrl;
        const examplePhotoAlt = this.examplePhotoAlt;
        const characterNumber = this.characterNumber;
        const stepNumber = this.stepNumber;
        const s = this.strings;

        this.className = "pbk-section";
        this.id = `pbk-character-section-${sectionId}`;

        // Generate unique IDs for all fields
        const childNameId = this.getFieldId("pbk-child-name");
        const childAgeId = this.getFieldId("pbk-child-age");
        const genderName = this.getFieldId("pbk-gender");
        const eyeName = this.getFieldId("pbk-eye");
        const hairName = this.getFieldId("pbk-hair");
        const hairColorName = this.getFieldId("pbk-hair-color");
        const faceFileId = this.getFieldId("pbk-face-file");
        const faceUrlId = this.getFieldId("pbk-face-url");

        this.innerHTML = `
          ${
            showStartBadge
              ? `<div class="pbk-section-start-badge">🎯 ${s.startHere || "Start here"}</div>`
              : ""
          }
          <div class="pbk-section-header">
            <span class="pbk-section-step">${stepNumber}</span>
            <h2 class="pbk-section-title">${s.character || "Character"} ${
              characterNumber ? characterNumber : ""
            }</h2>
          </div>
          <p class="pbk-section-description">${s.characterDescription || "Describe the child who will be the hero of the book"}</p>
          
          <div class="contact__fields">
            <!-- Name/Age row -->
            <div class="flex flex-wrap md:flex-nowrap gap-6 md:gap-8">
              <!-- Child name -->
              <div class="field" style="display: flex; flex-direction: column; flex: 1 1 100%; min-width: 0;">
                <input class="input is-floating" type="text" id="${childNameId}" name="child-name" placeholder="${s.childName || "Child's name"}" required>
                <label class="label is-floating" for="${childNameId}">${s.childName || "Child's name"}</label>
              </div>

              <!-- Age (number) -->
              <div class="field" style="display: flex; flex-direction: column; flex: 1 1 100%; min-width: 0;">
                <input class="input is-floating" type="number" min="0" max="99" step="1" id="${childAgeId}" name="child-age" placeholder="${s.childAge || "Child's age"}" required>
                <label class="label is-floating" for="${childAgeId}">${s.childAge || "Child's age"}</label>
              </div>
            </div>

            <!-- Gender/Eye color row -->
            <div class="flex flex-wrap gap-6 md:gap-8 mt-6">
              <!-- Gender radios -->
              <div class="field flex-1 min-w-0">
                <div class="pbk-caption" style="margin-bottom: 8px; font-weight: 500; opacity: 0.9;">${s.gender || "Gender"}</div>
                <div class="pbk-radio-row" role="radiogroup" aria-label="${s.gender || "Gender"}">
                  <input type="radio" name="${genderName}" id="${genderName}-male" value="male" required>
                  <label for="${genderName}-male"><span>${s.genderMale || "boy"}</span></label>

                  <input type="radio" name="${genderName}" id="${genderName}-female" value="female">
                  <label for="${genderName}-female"><span>${s.genderFemale || "girl"}</span></label>
                </div>
              </div>

              <!-- Eye color radios -->
              <div class="field flex-1 min-w-0">
                <div class="pbk-caption" style="margin-bottom: 8px; font-weight: 500; opacity: 0.9;">${s.eyeColor || "Eye color"}</div>
                <div class="pbk-radio-row" role="radiogroup" aria-label="${s.eyeColor || "Eye color"}">
                  <input type="radio" name="${eyeName}" id="${eyeName}-blue" value="blue" required>
                  <label for="${eyeName}-blue"><span>${s.eyeBlue || "blue"}</span></label>

                  <input type="radio" name="${eyeName}" id="${eyeName}-green" value="green">
                  <label for="${eyeName}-green"><span>${s.eyeGreen || "green"}</span></label>

                  <input type="radio" name="${eyeName}" id="${eyeName}-black" value="black">
                  <label for="${eyeName}-black"><span>${s.eyeBlack || "black"}</span></label>

                  <input type="radio" name="${eyeName}" id="${eyeName}-brown" value="brown">
                  <label for="${eyeName}-brown"><span>${s.eyeBrown || "brown"}</span></label>

                  <input type="radio" name="${eyeName}" id="${eyeName}-gray" value="gray">
                  <label for="${eyeName}-gray"><span>${s.eyeGray || "gray"}</span></label>
                </div>
              </div>
            </div>

            <!-- Hair length row -->
            <div class="field field--full mt-6">
              <div class="pbk-caption" style="margin-bottom: 8px; font-weight: 500; opacity: 0.9;">${s.hairLength || "Hair length"}</div>
              <div class="pbk-radio-row" role="radiogroup" aria-label="${s.hairLength || "Hair length"}">
                <input type="radio" name="${hairName}" id="${hairName}-bald" value="bald" required>
                <label for="${hairName}-bald"><span>${s.hairBald || "bald"}</span></label>

                <input type="radio" name="${hairName}" id="${hairName}-veryshort" value="very_short">
                <label for="${hairName}-veryshort"><span>${s.hairVeryShort || "very short"}</span></label>

                <input type="radio" name="${hairName}" id="${hairName}-ear" value="ear_length">
                <label for="${hairName}-ear"><span>${s.hairEar || "ear length"}</span></label>

                <input type="radio" name="${hairName}" id="${hairName}-shoulder" value="shoulder">
                <label for="${hairName}-shoulder"><span>${s.hairShoulder || "shoulder length"}</span></label>

                <input type="radio" name="${hairName}" id="${hairName}-chest" value="chest">
                <label for="${hairName}-chest"><span>${s.hairChest || "chest length"}</span></label>

                <input type="radio" name="${hairName}" id="${hairName}-waist" value="waist_very_long">
                <label for="${hairName}-waist"><span>${s.hairWaist || "waist - very long"}</span></label>
              </div>
            </div>

            <!-- Hair color row -->
            <div class="field field--full mt-6">
              <div class="pbk-caption" style="margin-bottom: 8px; font-weight: 500; opacity: 0.9;">${s.hairColor || "Hair color"}</div>
              <div class="pbk-radio-row" role="radiogroup" aria-label="${s.hairColor || "Hair color"}">
                <input type="radio" name="${hairColorName}" id="${hairColorName}-blonde" value="blonde" required>
                <label for="${hairColorName}-blonde"><span>${s.hairColorBlonde || "blonde"}</span></label>

                <input type="radio" name="${hairColorName}" id="${hairColorName}-dark-blonde" value="dark-blonde">
                <label for="${hairColorName}-dark-blonde"><span>${s.hairColorDarkBlonde || "dark blonde"}</span></label>

                <input type="radio" name="${hairColorName}" id="${hairColorName}-brown" value="brown">
                <label for="${hairColorName}-brown"><span>${s.hairColorBrown || "brown"}</span></label>

                <input type="radio" name="${hairColorName}" id="${hairColorName}-black" value="black">
                <label for="${hairColorName}-black"><span>${s.hairColorBlack || "black"}</span></label>

                <input type="radio" name="${hairColorName}" id="${hairColorName}-red" value="red">
                <label for="${hairColorName}-red"><span>${s.hairColorRed || "red"}</span></label>

                <input type="radio" name="${hairColorName}" id="${hairColorName}-other" value="other">
                <label for="${hairColorName}-other"><span>${s.hairColorOther || "other"}</span></label>
              </div>
            </div>

            <!-- Face photo upload (immediate S3) -->
            <div class="field field--full">
              <div class="pbk-upload-section">
                <label class="pbk-upload-label" for="${faceFileId}">
                  <div class="pbk-upload-content">
                    <div class="pbk-upload-icon">📷</div>
                    <div class="pbk-upload-text">${s.facePhoto || "Face photo"}</div>
                    <div class="pbk-upload-caption">${s.uploadClickOrDrag || "Click or drag file"}</div>
                    <div class="pbk-upload-format">${s.uploadFormat || "JPG, PNG (max 10MB)"}</div>
                  </div>
                </label>
                <input type="file" id="${faceFileId}" class="pbk-upload-input" accept="image/*" style="display: none;">
                <input type="hidden" id="${faceUrlId}" class="pbk-face-url" name="face-photo-url">
                
                <div class="pbk-photo-disclaimer">
                  <div class="pbk-photo-disclaimer-icon">ℹ️</div>
                  <div class="pbk-photo-disclaimer-content">
                    <strong>${s.photoTipsTitle || "Photo tips:"}</strong>
                    <p>${s.photoTipsContent || "Requirements for the photo..."}</p>
                    
                    ${
                      examplePhotoUrl
                        ? `
                    <div class="pbk-example-photo-wrapper">
                      <strong>${s.goodBadExamples || "Examples of good and bad photos:"}</strong>
                      <img src="${examplePhotoUrl}" alt="${examplePhotoAlt}" class="pbk-example-photo">
                    </div>
                    `
                        : ""
                    }
                  </div>
                </div>
              </div>

              <div class="pbk-character-preview${CHARACTER_FORM_PREVIEW_ENABLED && this.hasTurnstileSiteKey() ? "" : " pbk-hidden"}" data-character-preview>
                <button
                  type="button"
                  class="button button--secondary pbk-character-preview-btn"
                  data-preview-btn
                  disabled
                >
                  <span class="btn-fill" data-fill></span>
                  <span class="btn-text" data-preview-btn-text>${s.previewCharacter || "Preview character"}</span>
                </button>
                <p class="pbk-character-preview-hint pbk-hidden" data-preview-hint aria-live="polite">
                  ${s.previewFillRequired || "Upload a face photo, select eye color, and enter age to preview."}
                </p>
                <div
                  class="turnstile-widget pbk-character-preview-turnstile"
                  data-turnstile-widget
                  data-turnstile-action="create_preview"
                  data-turnstile-mode="invisible"
                  data-preview-turnstile
                ></div>
                <p class="pbk-character-preview-status pbk-hidden" data-preview-status aria-live="polite"></p>
                <div class="pbk-character-preview-result pbk-hidden" data-preview-result>
                  <img alt="${s.previewCharacterImageAlt || "Character preview"}" data-preview-image loading="lazy">
                </div>
              </div>
            </div>
          </div>
        `;
      }

      setupEventListeners() {
        const faceFileId = this.getFieldId("pbk-face-file");
        const faceUrlId = this.getFieldId("pbk-face-url");

        const faceFileInput = this.querySelector(`#${faceFileId}`);
        const faceUrlInput = this.querySelector(`#${faceUrlId}`);
        const faceUploadLabel = this.querySelector(
          `label.pbk-upload-label[for="${faceFileId}"]`,
        );

        // Face photo upload handler
        faceFileInput?.addEventListener("change", async (e) => {
          const file = e.target.files[0];
          if (!file) return;

          try {
            // Show loading state
            const uploadText =
              faceUploadLabel?.querySelector(".pbk-upload-text");
            const uploadCaption = faceUploadLabel?.querySelector(
              ".pbk-upload-caption",
            );
            if (uploadText) {
              uploadText.textContent =
                this.strings.uploadUploading || "Uploading...";
            }
            faceUploadLabel?.classList.add("pbk-upload-loading");

            // Step 1: Get presigned URL from API
            const response = await window.ChildbookStorefrontSession.fetch(
              "https://www.childbook.ai/api/upload-child-photo",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  fileName: file.name,
                  contentType: file.type,
                }),
              },
            );

            if (!response.ok) {
              throw new Error(
                `Failed to get upload URL: ${response.statusText}`,
              );
            }

            const { putUrl, key } = await response.json();

            // Step 2: Upload file to S3 using presigned URL
            const uploadResponse = await fetch(putUrl, {
              method: "PUT",
              body: file,
              headers: {
                "Content-Type": file.type,
              },
            });

            if (!uploadResponse.ok) {
              throw new Error(
                `Failed to upload file: ${uploadResponse.statusText}`,
              );
            }

            // const s3Key = `s3://childbook-photo-uploads/${key}`;
            // const base64S3Key = this.base64Encode(s3Key);

            // Step 3: Store the key (S3 path) for later use
            // faceUrlInput.value = `https://images.childbook.ai/sig/${base64S3Key}`;
            faceUrlInput.value = key;

            // Keep the headless preview component in sync with the new photo.
            this.updatePreviewData();

            // Update UI to show success with image preview
            const reader = new FileReader();
            const changePhotoText =
              this.strings.uploadChangePhoto || "Change photo";
            reader.onload = (e) => {
              if (faceUploadLabel) {
                const uploadContent = faceUploadLabel.querySelector(
                  ".pbk-upload-content",
                );
                if (uploadContent) {
                  uploadContent.innerHTML = `
                    <div class="pbk-upload-preview">
                      <img src="${e.target.result}" alt="Preview" class="pbk-upload-preview-image">
                      <div class="pbk-upload-preview-name">${file.name}</div>
                      <div class="pbk-upload-change-btn">
                        <span>🔄</span>
                        <span>${changePhotoText}</span>
                      </div>
                    </div>
                  `;
                }
              }
            };
            reader.readAsDataURL(file);

            faceUploadLabel?.classList.remove("pbk-upload-loading");
            faceUploadLabel?.classList.add("pbk-upload-selected");

            // Dispatch custom event to notify parent
            this.dispatchEvent(
              new CustomEvent("character-form:photo-uploaded", {
                bubbles: true,
                detail: { url: key, key, file },
              }),
            );
          } catch (error) {
            console.error("Error uploading photo:", error);

            // Show error state
            const s = this.strings;
            const uploadContent = faceUploadLabel?.querySelector(
              ".pbk-upload-content",
            );
            if (uploadContent) {
              uploadContent.innerHTML = `
                <div class="pbk-upload-icon">⚠️</div>
                <div class="pbk-upload-text">${s.uploadError || "Upload error"}</div>
                <div class="pbk-upload-caption">${s.uploadRetry || "Click to try again"}</div>
                <div class="pbk-upload-format">${s.uploadFormat || "JPG, PNG (max 10MB)"}</div>
              `;
            }
            faceUploadLabel?.classList.remove("pbk-upload-loading");
            faceUploadLabel?.classList.add("pbk-upload-error");

            // Clear the file input so user can retry
            faceFileInput.value = "";

            // Dispatch error event
            this.dispatchEvent(
              new CustomEvent("character-form:photo-error", {
                bubbles: true,
                detail: { error: error.message, file },
              }),
            );
          }
        });

        // Dispatch change events for form validation
        const inputs = this.querySelectorAll(
          'input[type="text"], input[type="number"], input[type="radio"]',
        );
        inputs.forEach((input) => {
          input.addEventListener("change", () => {
            this.updatePreviewData();
            this.dispatchEvent(
              new CustomEvent("character-form:change", {
                bubbles: true,
                detail: this.getFormData(),
              }),
            );
          });
        });
      }

      // Headless preview component wiring. The <character-preview> element
      // renders nothing; it exposes requestPhoto()/onImage/state for consumers.
      setupPreview() {
        this.preview = document.createElement("character-preview");
        this.appendChild(this.preview);
        this.previewContainer = this.querySelector("[data-character-preview]");
        this.previewBtn = this.querySelector("[data-preview-btn]");
        this.previewBtnText = this.querySelector("[data-preview-btn-text]");
        this.previewHint = this.querySelector("[data-preview-hint]");
        this.previewStatus = this.querySelector("[data-preview-status]");
        this.previewResult = this.querySelector("[data-preview-result]");
        this.previewImage = this.querySelector("[data-preview-image]");
        this.previewTurnstile = this.querySelector("[data-preview-turnstile]");
        if (!this.hasTurnstileSiteKey()) {
          this.previewContainer?.classList.add("pbk-hidden");
          this.updatePreviewData();
          return;
        }

        this.previewContainer?.classList.remove("pbk-hidden");
        window.theme?.turnstile
          ?.renderWidgets?.()
          ?.catch?.((error) => console.error(error));
        this.previewBtn?.addEventListener("click", () => {
          this.handlePreviewButtonClick();
        });
        this.preview?.addEventListener("character-preview:state", (event) => {
          this.syncPreviewButtonState(event.detail?.state);
        });
        this.updatePreviewData();
      }

      updatePreviewData() {
        if (!this.preview) return;
        const data = this.getFormData();
        this.preview.data = {
          illustrationId: CHARACTER_FORM_PREVIEW_DEFAULTS.magic.illustrationId,
          characterId: CHARACTER_FORM_PREVIEW_DEFAULTS.magic.characterId,
          faceUrl: data.faceUrl,
          eyeColor: data.eye,
          age: data.age,
          turnstileContainer: this.previewTurnstile,
        };
        if (this.preview?.state !== "loading") {
          this.clearPreviewResult();
        }
        this.updatePreviewButtonState();
      }

      canRequestPreview() {
        if (!this.preview) return false;
        const { faceUrl, eyeColor, age } = this.preview.data;
        return !!(
          this.hasTurnstileSiteKey() &&
          faceUrl &&
          eyeColor &&
          Number.isFinite(typeof age === "number" ? age : Number(age))
        );
      }

      hasTurnstileSiteKey() {
        return !!window.theme?.turnstile?.siteKey;
      }

      updatePreviewButtonState() {
        const canRequest = this.canRequestPreview();
        const isLoading = this.preview?.state === "loading";
        if (this.previewBtn) {
          this.previewBtn.disabled = !canRequest || isLoading;
        }
        this.previewHint?.classList.toggle(
          "pbk-hidden",
          canRequest || isLoading,
        );
      }

      syncPreviewButtonState(state) {
        const s = this.strings;
        const isLoading = state === "loading";
        if (this.previewBtn) {
          this.previewBtn.disabled = !this.canRequestPreview() || isLoading;
        }
        if (this.previewBtnText) {
          this.previewBtnText.textContent = isLoading
            ? s.previewGenerating || "Generating preview..."
            : s.previewCharacter || "Preview character";
        }
        this.previewHint?.classList.toggle(
          "pbk-hidden",
          this.canRequestPreview() || isLoading,
        );
      }

      clearPreviewResult() {
        this.previewResult?.classList.add("pbk-hidden");
        this.previewStatus?.classList.add("pbk-hidden");
        if (this.previewImage) {
          this.previewImage.removeAttribute("src");
        }
        if (this.previewStatus) {
          this.previewStatus.textContent = "";
          this.previewStatus.classList.remove(
            "pbk-character-preview-status--error",
          );
        }
      }

      showPreviewResult(imageUrl) {
        if (!this.previewImage || !this.previewResult) return;
        this.previewImage.src = imageUrl;
        this.previewResult.classList.remove("pbk-hidden");
        this.previewStatus?.classList.add("pbk-hidden");
      }

      showPreviewError(message) {
        if (!this.previewStatus) return;
        this.previewStatus.textContent =
          message ||
          this.strings.previewError ||
          "Could not generate preview. Please try again.";
        this.previewStatus.classList.remove("pbk-hidden");
        this.previewStatus.classList.add("pbk-character-preview-status--error");
      }

      async handlePreviewButtonClick() {
        if (!this.canRequestPreview() || this.preview?.state === "loading") {
          return;
        }

        this.clearPreviewResult();

        try {
          const imageUrl = await this.requestPreviewPhoto();
          this.showPreviewResult(imageUrl);
        } catch (error) {
          if (error?.name === "AbortError") {
            return;
          }

          console.error("Error generating character preview:", error);
          const message = error?.message?.includes(
            "Missing required preview data",
          )
            ? this.strings.previewFillRequired
            : this.strings.previewError;
          this.showPreviewError(message);
        } finally {
          this.updatePreviewButtonState();
          this.syncPreviewButtonState(this.preview?.state || "idle");
        }
      }

      // Caller-facing hook for UI that wants to trigger a preview request.
      // Returns a Promise that resolves with the generated image URL.
      requestPreviewPhoto() {
        this.updatePreviewData();
        if (!this.preview) {
          return Promise.reject(new Error("Preview component is not ready"));
        }
        return this.preview.requestPhoto();
      }

      getFormData() {
        const childNameId = this.getFieldId("pbk-child-name");
        const childAgeId = this.getFieldId("pbk-child-age");
        const genderName = this.getFieldId("pbk-gender");
        const eyeName = this.getFieldId("pbk-eye");
        const hairName = this.getFieldId("pbk-hair");
        const hairColorName = this.getFieldId("pbk-hair-color");
        const faceUrlId = this.getFieldId("pbk-face-url");

        return {
          name: this.querySelector(`#${childNameId}`)?.value.trim() || "",
          age: this.querySelector(`#${childAgeId}`)?.value.trim() || "",
          gender:
            this.querySelector(`input[name="${genderName}"]:checked`)?.value ||
            "",
          eye:
            this.querySelector(`input[name="${eyeName}"]:checked`)?.value || "",
          hair:
            this.querySelector(`input[name="${hairName}"]:checked`)?.value ||
            "",
          hairColor:
            this.querySelector(`input[name="${hairColorName}"]:checked`)
              ?.value || "",
          faceUrl: this.querySelector(`#${faceUrlId}`)?.value || "",
          characterNumber: this.characterNumber || "1",
        };
      }

      isValid() {
        const data = this.getFormData();
        return !!(
          data.name &&
          data.age &&
          data.gender &&
          data.eye &&
          data.hair &&
          data.hairColor &&
          data.faceUrl
        );
      }

      reset() {
        const childNameId = this.getFieldId("pbk-child-name");
        const childAgeId = this.getFieldId("pbk-child-age");
        const faceFileId = this.getFieldId("pbk-face-file");
        const faceUrlId = this.getFieldId("pbk-face-url");

        // Clear text inputs
        const nameInput = this.querySelector(`#${childNameId}`);
        const ageInput = this.querySelector(`#${childAgeId}`);
        if (nameInput) nameInput.value = "";
        if (ageInput) ageInput.value = "";

        // Uncheck radio buttons
        const radioInputs = this.querySelectorAll('input[type="radio"]');
        radioInputs.forEach((radio) => (radio.checked = false));

        // Reset file upload
        const faceFileInput = this.querySelector(`#${faceFileId}`);
        const faceUrlInput = this.querySelector(`#${faceUrlId}`);
        const faceUploadLabel = this.querySelector(
          `label.pbk-upload-label[for="${faceFileId}"]`,
        );

        if (faceFileInput) faceFileInput.value = "";
        if (faceUrlInput) faceUrlInput.value = "";

        // Reset upload label to initial state
        const s = this.strings;
        const uploadContent = faceUploadLabel?.querySelector(
          ".pbk-upload-content",
        );
        if (uploadContent) {
          uploadContent.innerHTML = `
            <div class="pbk-upload-icon">📷</div>
            <div class="pbk-upload-text">${s.facePhoto || "Face photo"}</div>
            <div class="pbk-upload-caption">${s.uploadClickOrDrag || "Click or drag file"}</div>
            <div class="pbk-upload-format">${s.uploadFormat || "JPG, PNG (max 10MB)"}</div>
          `;
        }
        faceUploadLabel?.classList.remove(
          "pbk-upload-selected",
          "pbk-upload-error",
          "pbk-upload-loading",
        );

        this.clearPreviewResult();
        this.updatePreviewData();
        this.syncPreviewButtonState("idle");
      }

      base64Encode(text) {
        try {
          const path = text.split("/");
          const fileName = path.pop();
          if (!fileName) {
            return text;
          }
          let safeKey = `${path.join("/")}/${encodeURIComponent(fileName)}`;

          safeKey = safeKey.replaceAll("%20", " ");
          return btoa(safeKey);
        } catch (error) {
          return text;
        }
      }
    },
  );
}
